import { Layout } from 'components/layout';
import { NextPage } from 'next';
import { Button, Flex, Heading, Loader, Text } from '../../../components/ui';
import { globalTheme } from '../../../styles/global-theme';
import styled, { css } from 'styled-components';
import { breakpoints } from '../../../styles/breakpoints';
import {
  Booking,
  BookingConfigSettings,
  HistoricalBooking,
} from '../../../lib/api/generated';
import { useMedia } from 'react-use';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import Image from 'next/image';
import { BookingPrivateItem } from 'components/booking-confirmation';
import React, { useEffect, useMemo, useState } from 'react';
import { useBookingHistory } from '../../../hooks/use-booking-history';
import {
  getChangedValue,
  hasValueChanged,
} from '../../../components/booking-confirmation/confirmation-utils';
import { withAuth } from '../../../components/with-auth';
import { BookingReadHistory } from '../../../hooks/use-read-bookings-status';
import { useUser } from '../../../hooks/use-user';
import { useBookingsConfig } from '../../../hooks/use-bookings-config';

const HiddenPrintContainer = styled.div`
  @media print {
    display: none;
  }
`;

const BookingContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ isMobile }) => (isMobile ? 30 : 60)}px;
  background-color: ${globalTheme.colors.lightGray};
  border-radius: 20px;
  @media print {
    margin: 0;
    padding: 0;
  }
`;

const ContentWrapper = styled.div`
  margin: 60px 80px;
  @media screen and (max-width: ${breakpoints.small}) {
    margin: 40px 10px;
  }

  @media print {
    margin: 0;
    padding: 0;
  }
`;

const Inline = styled.div<{ stackOnSmall?: boolean }>`
  display: flex;
  align-items: center;

  ${({ stackOnSmall }) =>
    stackOnSmall &&
    css`
      flex-direction: column;
    `}
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${globalTheme.space[2]} 0;
  gap: ${globalTheme.space[1]};
  flex-wrap: wrap;
  ${HiddenPrintContainer}
`;

const ImageWrapper = styled.div<{ hasTopMargin?: boolean }>`
  margin: 0 auto;
  margin-top: ${(props) => (props.hasTopMargin ? globalTheme.space[2] : '')};
  display: flex;
  align-items: flex-start;
  object-fit: contain;

  @media screen and (max-width: ${breakpoints.medium}) {
    max-width: 300px;
  }

  > img {
    border-radius: 6px;
    width: 100%;
    @media print {
      max-width: 200px;
      height: auto;
    }
  }
`;

const MainHeading = styled(Heading)<{ isMobile?: boolean }>`
  font-weight: ${globalTheme.fontWeights.semiBold};
  font-size: ${globalTheme.fontSizes['6']};
  width: ${({ isMobile }) => (!isMobile ? '100%' : 'inherit')};
  text-align: ${({ isMobile }) => (!isMobile ? 'center' : 'inherit')};
`;

const BackButton = styled(Button)`
  margin: 20px 80px 0;
  color: black;
  background: none;
  &:hover {
    color: black;
    background: none;
  }

  @media screen and (max-width: ${breakpoints.medium}) {
    margin: 20px;
  }
`;

const ConfirmButton = styled(Button)`
  @media print {
    display: none;
  }
`;

const HeaderWrapper = styled(Flex)<{
  $backgroundColor?: keyof typeof globalTheme.colors;
}>`
  margin-top: ${globalTheme.space[5]};
  padding: ${globalTheme.space[2]} 80px;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor && globalTheme.colors[$backgroundColor]};
`;

const BookingOrgView: NextPage = () => {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);
  const router = useRouter();
  const { id, historyId, previousHistoryId } = router.query;
  const [data, setData] = useState<HistoricalBooking | undefined>();
  const [dataArray, setDataArray] = useState<any | undefined>();
  const [prevDataArray, setPrevDataArray] = useState<any | undefined>();

  const { data: user } = useUser();

  // Get booking config data
  const { data: configData, isLoading: configLoading } = useBookingsConfig();

  const {
    data: bookingData,
    isLoading: bookingIsLoading,
    error: bookingError,
  } = useBookingHistory(
    id ? (id as string) : '',
    historyId ? (historyId as string) : ''
  );

  const {
    data: previousBookingData,
    isLoading: previousBookingIsLoading,
    error: previousBookingError,
  } = useBookingHistory(
    id ? (id as string) : '',
    previousHistoryId ? (previousHistoryId as string) : ''
  );

  useEffect(() => {
    if (bookingData) {
      setData(bookingData);

      setDataArray(
        Object.entries(bookingData.data).map(([key, value]) => {
          return {
            key,
            value,
          };
        })
      );
    }
  }, [bookingData]);

  useEffect(() => {
    if (previousBookingData) {
      setPrevDataArray(
        Object.entries(previousBookingData.data).map(([key, value]) => {
          return {
            key,
            value,
          };
        })
      );
    }
  }, [bookingData]);

  const setReadBookingHandler = (booking: HistoricalBooking) => {
    const bookingMatch = JSON.parse(
      localStorage.getItem('booking-data')!
    )?.find((bookingRow: BookingReadHistory) => bookingRow.id === booking.id);

    const updatedSoloBookingData = {
      ...bookingMatch,
      readHistory: [
        ...bookingMatch.readHistory,
        {
          readHistoryId: booking?.history?.id,
          readTime: Date.now(),
        },
      ],
    };

    const bookingMap = JSON.parse(localStorage.getItem('booking-data')!).map(
      (bookingI: Booking) => {
        if (booking.id === bookingI.id) {
          return updatedSoloBookingData;
        } else {
          return bookingI;
        }
      }
    );

    localStorage.setItem('booking-data', JSON.stringify(bookingMap));

    router.back();
  };

  const setUnReadBookingHandler = (booking: HistoricalBooking) => {
    const bookingMatch = JSON.parse(
      localStorage.getItem('booking-data')!
    )?.find((bookingRow: BookingReadHistory) => bookingRow.id === booking.id);

    const readHistoryCopy = bookingMatch?.readHistory;
    readHistoryCopy.pop();

    const updatedSoloBookingData = {
      ...bookingMatch,
      readHistory: readHistoryCopy,
    };

    const bookingMap = JSON.parse(localStorage.getItem('booking-data')!).map(
      (bookingI: Booking) => {
        if (booking.id === bookingI.id) {
          return updatedSoloBookingData;
        } else {
          return bookingI;
        }
      }
    );

    localStorage.setItem('booking-data', JSON.stringify(bookingMap));

    router.back();
  };

  const userEditedBooking = (
    booking: HistoricalBooking,
    userId: string | undefined
  ): boolean => {
    const bookingMatch = JSON.parse(
      localStorage.getItem('booking-data')!
    )?.find((bookingRow: BookingReadHistory) => bookingRow?.id === booking?.id);

    return userId === bookingMatch?.lastEditedUserId;
  };

  const handleBack = () => {
    router.back();
  };

  const readTimeGetter = (booking: HistoricalBooking): string | null => {
    const bookingMatch = JSON.parse(
      localStorage.getItem('booking-data')!
    )?.find((bookingRow: BookingReadHistory) => bookingRow.id === booking?.id);

    const latestReadBooking =
      !!bookingMatch?.readHistory?.length &&
      bookingMatch?.readHistory[bookingMatch?.readHistory?.length - 1];

    return bookingMatch?.latestId === latestReadBooking?.readHistoryId
      ? latestReadBooking?.readTime
      : null;
  };

  const formattedDate = (date: number | Date | null | undefined) => {
    if (date) {
      return format(date, 'eee, d MMM HH:mm a');
    }
  };

  const latestBookingRead = useMemo(() => {
    const booking = JSON.parse(localStorage.getItem('booking-data')!).find(
      (booking: BookingReadHistory) => {
        return booking?.id === bookingData?.id;
      }
    );

    return (
      (!!booking?.readHistory?.length &&
        booking?.readHistory?.pop().readHistoryId === booking?.latestId) ||
      user?.user_id === booking?.lastEditedUserId
    );
  }, [JSON.parse(localStorage.getItem('booking-data')!)]);

  if (!data && bookingError) {
    return (
      <Flex
        gap={0}
        direction='column'
        alignItems='center'
        justifyContent='center'
      >
        <Image
          src={require('../../../components/images/error-illustration.png')}
          width={80}
          height={80}
          alt='error-illustration'
        />
        <Flex direction='column' alignItems='center' justifyContent='center'>
          <Text fontSize={2} textAlign='center'>
            We caught an unexpected error while processing you request.
          </Text>
          <Text textAlign='center'>Please try again later.</Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Layout meta={{ title: 'Booking' }}>
      {!data && bookingIsLoading ? (
        <Loader floating />
      ) : (
        <>
          <BackButton
            size='small'
            iconLeft='LeftArrowIcon'
            onClick={handleBack}
          >
            Back
          </BackButton>

          {latestBookingRead && (
            <HeaderWrapper
              alignItems='center'
              gap={0}
              stackOnSmall
              $backgroundColor={'highlightBar'}
            >
              <Text
                fontWeight={'semiBold'}
                fontSize={1}
                style={{ marginLeft: globalTheme.space[0] }}
                maxWidth={'max-content'}
                color='black'
              >
                {userEditedBooking(bookingData!, user?.user_id)
                  ? 'Last edited by you'
                  : `Read -
                ${
                  !!readTimeGetter(bookingData!) &&
                  formattedDate(
                    new Date(readTimeGetter(bookingData!) as string)
                  )
                }`}
              </Text>
            </HeaderWrapper>
          )}

          <ContentWrapper>
            <BookingContainer isMobile={isMobile}>
              <ImageWrapper hasTopMargin={false}></ImageWrapper>
              <Inline
                style={{
                  padding: `${globalTheme.space[2]} 0 ${globalTheme.space[4]} 0`,
                  justifyContent: 'center',
                }}
                stackOnSmall
              >
                <MainHeading
                  level='h1'
                  style={{ fontWeight: globalTheme.fontWeights.semiBold }}
                  /* DO NOT REMOVE: the id is required by the Confirmation PDF generator */
                >
                  Booking details
                </MainHeading>
              </Inline>
              {!data ? (
                <Loader />
              ) : (
                <>
                  {dataArray
                    .filter((row: any) => {
                      return (
                        row?.key !== '_id' &&
                        row?.key !== '_count_total' &&
                        row?.key !== '_count_incoming' &&
                        row?.key !== '_count_outgoing' &&
                        !row?.key.includes('__') &&
                        row?.key !== '_date' &&
                        row?.key !== '_date_from' &&
                        row?.key !== '_date_to' &&
                        row?.key !== '_date_delivery_from' &&
                        row?.key !== '_date_delivery_to' &&
                        row?.key !== '_date_kill_from' &&
                        row?.key !== '_date_kill_to' &&
                        row?.key !== '_date_transport_from' &&
                        row?.key !== '_date_transport_to'
                      );
                    })
                    ?.map((booking: any, index: number) => {
                      if (booking.key === '_date_delivery') {
                        booking.key = configData?.settings.date_delivery_name;
                      }

                      if (booking.key === '_date_kill') {
                        booking.key = configData?.settings.date_kill_name;
                      }

                      if (booking.key === '_date_transport') {
                        booking.key = configData?.settings.date_transport_name;
                      }

                      return (
                        <BookingPrivateItem
                          isMobile={isMobile}
                          label={booking.key}
                          hasValueChanged={hasValueChanged(
                            booking.key,
                            prevDataArray,
                            dataArray
                          )}
                          previousRow={prevDataArray}
                          key={`${booking.key}_${index}`}
                          historyValue={getChangedValue(
                            booking.key,
                            prevDataArray
                          )}
                          currentValue={booking.value}
                        />
                      );
                    })}
                </>
              )}
            </BookingContainer>
            <ButtonWrapper>
              <ConfirmButton
                variant={latestBookingRead ? 'tertiaryOutlined' : 'primary'}
                onClick={() =>
                  latestBookingRead
                    ? setUnReadBookingHandler(bookingData!)
                    : setReadBookingHandler(bookingData!)
                }
                disabled={
                  !bookingData || userEditedBooking(bookingData!, user?.user_id)
                }
              >
                {latestBookingRead ? `Mark as unread` : `Mark as read`}
              </ConfirmButton>
            </ButtonWrapper>
          </ContentWrapper>
        </>
      )}
    </Layout>
  );
};

export default withAuth(BookingOrgView, {
  // setting hideHomeLink to false allows it to appear on summaryGrid pages
  hideNav: true,
  overrideHeaderContent: true,
  hideHomeLink: false,
});
